import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "../store/auth-slice";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useAuth } from "../context/AuthContext";
import { HOBBY_PRESETS, PROFESSION_PRESETS } from "../lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useCommon } from "../context/CommonContext";

const steps = [
	{ label: "Profile", icon: "👤" },
	{ label: "Identity", icon: "💼" },
	{ label: "About", icon: "📝" },
];

export default function OnboardingModal({ open, onClose }) {
	const { user } = useAuth();
	const { settings } = useCommon();
	const dispatch = useDispatch();
	const { isLoading } = useSelector((s) => s.auth);

	const inputRef = useRef(null);

	const professions = useMemo(() => {
		return settings?.profilePresets?.professions ?? PROFESSION_PRESETS;
	}, [settings]);

	const hobbies = useMemo(() => {
		return settings?.profilePresets?.hobbies ?? HOBBY_PRESETS;
	}, [settings]);

	const [step, setStep] = useState(0);
	const [form, setForm] = useState({
		username: "",
		address: "",
		profession: "",
		hobbies: [],
		bio: "",
	});

	useEffect(() => {
		if (!user) return;
		setForm({
			username: user.username || "",
			address: user.address || "",
			profession: user.profession || "",
			hobbies: user.hobbies || [],
			bio: user.bio || "",
		});
	}, [user]);

	useEffect(() => {
		inputRef.current?.focus();
	}, [step]);

	const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
	const prev = () => setStep((s) => Math.max(s - 1, 0));

	const toggleHobby = (h) => {
		setForm((prev) => ({
		...prev,
		hobbies: prev.hobbies.includes(h)
			? prev.hobbies.filter((x) => x !== h)
			: [...prev.hobbies, h],
		}));
	};

	const handleSubmit = async () => {
		if (/\s/.test(form.username)) return;

		await dispatch(
			updateUser({
				name: user.name,
				...form,
			})
		);

		onClose?.();
	};

	if (!open) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
			<motion.div
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				className="w-full max-w-xl rounded-3xl bg-white/80 backdrop-blur-xl shadow-2xl border border-white/20 p-8 relative"
			>
				<div className="mb-8">
					<h2 className="text-3xl font-bold tracking-tight">
						Let’s build your profile
					</h2>
					<p className="text-gray-500 text-sm mt-1">
						Takes less than 30 seconds
					</p>

					<div className="mt-6">
						<div className="h-1 w-full bg-gray-200 rounded-full overflow-hidden">
						<motion.div
							className="h-full bg-gradient-to-r from-green-400 to-emerald-600"
							initial={{ width: 0 }}
							animate={{
								width: `${((step + 1) / steps.length) * 100}%`,
							}}
							transition={{ duration: 0.3 }}
						/>
						</div>
					</div>
				</div>

				<AnimatePresence mode="wait">
					<motion.div
						key={step}
						ref={inputRef}
						initial={{ opacity: 0, x: 40 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: -40 }}
						transition={{ duration: 0.25 }}
						className="space-y-6"
					>
						{step === 0 && (
							<div className="space-y-4">
								<div>
									<p className="text-sm font-medium mb-1">Username</p>
									<Input
										className="h-11 rounded-xl"
										value={form.username}
										onChange={(e) =>
											setForm({
												...form,
												username: e.target.value.replace(/\s+/g, ""),
											})
										}
										placeholder="abhishek_dev"
									/>
								</div>

								<div>
									<p className="text-sm font-medium mb-1">Location</p>
									<Input
										className="h-11 rounded-xl"
										value={form.address}
										onChange={(e) =>
											setForm({ ...form, address: e.target.value })
										}
										placeholder="Guwahati, India"
									/>
								</div>
							</div>
						)}

						{step === 1 && (
							<>
								<div>
									<p className="text-sm font-medium mb-3">
										What do you do?
									</p>

									<div className="flex flex-wrap gap-2">
										{professions.map((p) => (
										<motion.button
											whileTap={{ scale: 0.95 }}
											key={p}
											onClick={() =>
												setForm({ ...form, profession: p })
											}
											className={`px-4 py-2 rounded-full text-sm font-medium border transition ${
											form.profession === p
												? "bg-black text-white border-black"
												: "hover:bg-gray-100"
											}`}
										>
											{p}
										</motion.button>
										))}
									</div>
								</div>

								<div>
									<p className="text-sm font-medium mb-3">
										Your interests
									</p>

									<div className="flex flex-wrap gap-2">
										{hobbies.map((h) => (
											<motion.button
												whileTap={{ scale: 0.9 }}
												key={h}
												onClick={() => toggleHobby(h)}
												className={`px-3 py-1.5 rounded-full text-sm border transition ${
												form.hobbies.includes(h)
													? "bg-gradient-to-r from-purple-500 to-pink-500 text-white border-none"
													: "hover:bg-gray-100"
												}`}
											>
												{h}
											</motion.button>
										))}
									</div>
								</div>
							</>
						)}

						{/* STEP 3 */}
						{step === 2 && (
							<div>
								<p className="text-sm font-medium mb-2">About you</p>

								<Textarea
									className="rounded-xl min-h-[120px]"
									value={form.bio}
									onChange={(e) =>
										setForm({ ...form, bio: e.target.value })
									}
									placeholder="I build cool stuff, love gaming & startups"
								/>

								<p className="text-xs text-gray-400 mt-1">
									Keep it short and interesting
								</p>
							</div>
						)}
					</motion.div>
				</AnimatePresence>

				<div className="flex justify-between items-center mt-10">
					{step > 0 ? (
						<button
							onClick={prev}
							className="text-sm text-gray-500 hover:text-black"
						>
							← Back
						</button>
					) : (
						<div />
					)}

					{step < steps.length - 1 ? (
						<Button className="rounded-xl px-6" onClick={next}>
							Continue →
						</Button>
					) : (
						<Button
							onClick={handleSubmit}
							disabled={isLoading}
							className="rounded-xl px-6 bg-black text-white hover:bg-gray-900"
						>
							{isLoading ? "Saving..." : "Finish"}
						</Button>
					)}
				</div>
			</motion.div>
		</div>
	);
}